import React, { useEffect, useRef, useState } from "react";
import { RESEND_COOLDOWN_SECONDS } from "../authUtils";
import { askAgainText, normalizeResult, shortCountdown, LIVE_CLOCK_MAX_SECONDS } from "../waitText";

const MESSAGES = {
  sent: "Sent again. Check your inbox and spam folder.",
  error: "Couldn't send it right now. Please try again shortly.",
  rate_limited: "Too many requests. Please wait a few minutes and try again.",
};

// "Send the email again" for the dialogs that follow a verification or reset email.
// An email was just sent when this appears, so the button starts locked, and one timer unlocks it at the moment
// the wait ends. A short wait (up to two minutes) also shows a live countdown, ticking once a second and only
// until it ends. A long wait (the hourly limit) is one fixed line: nothing ticks for 40 minutes.
// onResend resolves "sent", "rate_limited", "error", "verified", or {status, retryAfter} with retryAfter in seconds.
export default function ResendButton({ onResend, seconds = RESEND_COOLDOWN_SECONDS, label = "Send the email again" }) {
  const [endAt, setEndAt] = useState(() => Date.now() + seconds * 1000);
  const [waitSeconds, setWaitSeconds] = useState(seconds); // length of the current wait
  const [left, setLeft] = useState(seconds); // seconds left, kept fresh only for short waits
  const [note, setNote] = useState(() => askAgainText(seconds)); // the fixed line for long waits
  const [waiting, setWaiting] = useState(seconds > 0);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // null | { kind, text }
  const mounted = useRef(true);
  const live = waitSeconds <= LIVE_CLOCK_MAX_SECONDS;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // One timeout for the whole wait. A sleeping tab can run it late, so waking up checks the clock too.
  useEffect(() => {
    const remaining = endAt - Date.now();
    if (remaining <= 0) {
      setWaiting(false);
      return undefined;
    }
    setWaiting(true);
    const timer = setTimeout(() => setWaiting(false), remaining + 20);
    const wake = () => {
      if (Date.now() >= endAt) setWaiting(false);
    };
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("focus", wake);
    };
  }, [endAt]);

  // The live countdown: once a second, only for a short wait, and it stops itself when the wait ends.
  useEffect(() => {
    if (!waiting || !live) return undefined;
    const tick = () => setLeft(Math.max(0, Math.ceil((endAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [waiting, live, endAt]);

  const startWait = (secs) => {
    setNote(askAgainText(secs));
    setWaitSeconds(secs);
    setLeft(secs);
    setEndAt(Date.now() + secs * 1000);
  };

  const click = async () => {
    if (waiting || busy || Date.now() < endAt) return;
    setBusy(true);
    setStatus(null);
    let result;
    try {
      result = normalizeResult(await onResend());
    } catch (_) {
      result = { status: "error" };
    }
    if (!mounted.current) return;
    setBusy(false);
    if (result.status === "verified") return;
    if (result.status === "sent") {
      setStatus({ kind: "sent", text: MESSAGES.sent });
      startWait(seconds);
    } else if (result.status === "rate_limited") {
      const text = result.retryAfter
        ? `You've asked for several emails. ${askAgainText(result.retryAfter)}`
        : MESSAGES.rate_limited;
      setStatus({ kind: "rate_limited", text });
      startWait(result.retryAfter || seconds);
    } else {
      setStatus({ kind: "error", text: MESSAGES.error });
    }
  };

  const waitLine = live ? `You can ask again in ${shortCountdown(left)}.` : note;

  return (
    <div className="rg-modal-resend">
      <button type="button" className="rg-modal-link" onClick={click} disabled={waiting || busy}>
        {busy ? "Sending..." : label}
      </button>
      {status ? (
        <p className="rg-modal-status" role="status">
          {status.text}
          {status.kind === "sent" && waiting ? ` ${waitLine}` : ""}
        </p>
      ) : waiting ? (
        // aria-live off: the ticking number is not announced every second
        <p className="rg-modal-status" aria-live="off">
          {waitLine}
        </p>
      ) : null}
    </div>
  );
}
